import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import { Check } from 'lucide-react';
import { CATEGORY_LABEL, ALL_CATEGORIES, isWeightOnly } from './constants';

const StoneEntryDialog = ({
  open, onOpenChange, form, setForm, editIndex, onSave,
  disabledInsertCats, // array of insert categories that are disabled (plain-gold rule)
}) => {
  const weightOnly = isWeightOnly(form?.category);
  const disabled = disabledInsertCats || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editIndex >= 0 ? 'Edit entry' : 'Add entry'}</DialogTitle>
          <DialogDescription>
            Diamond / Stone / Beads&nbsp;&amp;&nbsp;Pearls: pcs + wt + usability.
            Dust / Others: weight only.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Category</Label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {ALL_CATEGORIES.map((c) => {
                const isDisabled = disabled.includes(c);
                return (
                  <button
                    key={c}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => setForm({ ...form, category: c })}
                    className={`h-11 text-xs rounded-lg border transition-colors ${
                      isDisabled
                        ? 'bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed'
                        : form.category === c
                          ? 'bg-amber-700 text-white border-amber-800'
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`entry-cat-${c}`}
                    title={isDisabled ? 'Receipt declared this piece as plain gold — this category is locked.' : ''}
                  >
                    {CATEGORY_LABEL[c]}
                  </button>
                );
              })}
            </div>
            {disabled.length > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">
                Plain-gold piece — only Dust / Others can be recorded.
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs">Weight (gm)</Label>
            <Input
              type="number" inputMode="decimal" step="0.001" min="0"
              value={form.wt}
              onChange={(e) => setForm({ ...form, wt: e.target.value })}
              data-testid="entry-wt-input"
              className="h-11 text-base"
              autoFocus
            />
          </div>
          {!weightOnly && (
            <>
              <div>
                <Label className="text-xs">Pieces (count)</Label>
                <Input
                  type="number" inputMode="numeric" min="0"
                  value={form.pcs}
                  onChange={(e) => setForm({ ...form, pcs: e.target.value })}
                  data-testid="entry-pcs-input"
                  className="h-11 text-base"
                  placeholder="How many pcs?"
                />
              </div>
              <div>
                <Label className="text-xs">Usability</Label>
                <div className="grid grid-cols-3 gap-2 mt-1">
                  {[
                    { key: 'usable',     label: 'Usable',     on: 'bg-emerald-700 border-emerald-800' },
                    { key: 'broken',     label: 'Broken',     on: 'bg-orange-600  border-orange-700'  },
                    { key: 'not_usable', label: 'Not usable', on: 'bg-rose-700    border-rose-800'    },
                  ].map(({ key, label, on }) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm({ ...form, usability: key })}
                      className={`h-11 text-xs rounded-lg border transition-colors ${
                        form.usability === key
                          ? `${on} text-white`
                          : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                      }`}
                      data-testid={`entry-usability-${key}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
          {weightOnly && (
            <div className="text-[11px] text-slate-500">
              {CATEGORY_LABEL[form.category]} is weight-only — no piece count or usability.
            </div>
          )}
        </div>
        <div className="pt-3 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 h-11">Cancel</Button>
          <Button onClick={onSave} className="flex-1 h-11 bg-amber-700 hover:bg-amber-800" data-testid="entry-save-btn">
            <Check className="w-4 h-4 mr-1" /> Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoneEntryDialog;
